import { StorageService } from './storage';
import { PIN_LOCKOUT_MS } from '../config/authTimeouts';

/**
 * PIN Service - Secure PIN-based authentication
 *
 * Architecture:
 * - PIN is hashed with PBKDF2 + random salt (100,000 iterations)
 * - Supports 4-6 digit PINs
 * - Lock-on-start functionality
 * - Rate limiting to prevent brute force attacks
 * - Biometric auth is OPTIONAL on top of PIN
 */

interface PINConfig {
  pinHash: string;
  salt: string; // Base64-encoded random salt
  iterations: number; // PBKDF2 iteration count
  createdAt: string;
  lockOnStart: boolean;
}

// 🔒 SECURITY: Rate limiting configuration
interface RateLimitState {
  failedAttempts: number;
  lockedUntil: number | null;
  lastAttemptTime: number;
}

const MAX_ATTEMPTS = 3;
// PIN lockout is 15 min — much longer than login lockout (1 min) because
// a PIN attacker has the device. Sourced from the central config so it
// stays in sync with related auth flows.
const LOCKOUT_DURATION = PIN_LOCKOUT_MS;
const ATTEMPT_DELAY = 2000; // 2 seconds delay between attempts

// 🔒 SECURITY: PBKDF2 configuration
const PBKDF2_ITERATIONS = 100000; // 100,000 iterations (OWASP recommended minimum)
const SALT_LENGTH = 32; // 256 bits

class PINService {
  private isLocked = false;
  private pinHash: string | null = null;
  private rateLimitState: RateLimitState = {
    failedAttempts: 0,
    lockedUntil: null,
    lastAttemptTime: 0
  };

  /**
   * Initialize PIN service
   */
  init(): boolean {
    console.log('🔢 Initializing PIN service...');

    // Load saved PIN config
    const config = StorageService.get<PINConfig>('pin_config');
    if (config) {
      this.pinHash = config.pinHash;
      console.log('✅ PIN configured');

      // Check if we already unlocked in this session
      const unlockedThisSession = sessionStorage.getItem('unlocked_session') === 'true';

      // Check if app should be locked on start (only if not already unlocked this session)
      if (config.lockOnStart && !unlockedThisSession) {
        this.isLocked = true;
        console.log('🔒 App locked on start (PIN required)');
      } else if (unlockedThisSession) {
        console.log('✅ Already unlocked in this session');
        this.isLocked = false;
      }
    } else {
      console.log('ℹ️ No PIN configured');
    }

    // 🔒 SECURITY: Load rate limit state from sessionStorage
    this.loadRateLimitState();

    return true;
  }

  /**
   * 🔒 SECURITY: Load rate limit state
   */
  private loadRateLimitState(): void {
    const saved = sessionStorage.getItem('pin_rate_limit');
    if (saved) {
      try {
        this.rateLimitState = JSON.parse(saved);
        // Check if lockout has expired
        if (this.rateLimitState.lockedUntil && Date.now() > this.rateLimitState.lockedUntil) {
          this.resetRateLimit();
        }
      } catch (e) {
        this.resetRateLimit();
      }
    }
  }

  /**
   * 🔒 SECURITY: Save rate limit state
   */
  private saveRateLimitState(): void {
    sessionStorage.setItem('pin_rate_limit', JSON.stringify(this.rateLimitState));
  }

  /**
   * 🔒 SECURITY: Reset rate limit state
   */
  private resetRateLimit(): void {
    this.rateLimitState = {
      failedAttempts: 0,
      lockedUntil: null,
      lastAttemptTime: 0
    };
    this.saveRateLimitState();
  }

  /**
   * 🔒 SECURITY: Check if rate limited
   */
  isRateLimited(): { limited: boolean; remainingTime?: number; reason?: string } {
    const now = Date.now();

    // Check if locked out
    if (this.rateLimitState.lockedUntil && now < this.rateLimitState.lockedUntil) {
      const remainingMs = this.rateLimitState.lockedUntil - now;
      const remainingMinutes = Math.ceil(remainingMs / 60000);
      return {
        limited: true,
        remainingTime: remainingMs,
        reason: `Trop de tentatives échouées. Réessayez dans ${remainingMinutes} minute(s).`
      };
    }

    // Check if attempt is too soon after last attempt
    if (this.rateLimitState.lastAttemptTime && (now - this.rateLimitState.lastAttemptTime) < ATTEMPT_DELAY) {
      const remainingMs = ATTEMPT_DELAY - (now - this.rateLimitState.lastAttemptTime);
      return {
        limited: true,
        remainingTime: remainingMs,
        reason: 'Veuillez patienter quelques secondes avant de réessayer.'
      };
    }

    return { limited: false };
  }

  /**
   * 🔒 SECURITY: Record failed attempt
   */
  private recordFailedAttempt(): void {
    this.rateLimitState.failedAttempts++;
    this.rateLimitState.lastAttemptTime = Date.now();

    // Lock out after MAX_ATTEMPTS
    if (this.rateLimitState.failedAttempts >= MAX_ATTEMPTS) {
      this.rateLimitState.lockedUntil = Date.now() + LOCKOUT_DURATION;
      console.warn(`🚨 Too many failed attempts. Locked out for ${LOCKOUT_DURATION / 60000} minutes.`);
    }

    this.saveRateLimitState();
  }

  /**
   * Check if PIN is configured
   */
  isPINConfigured(): boolean {
    return this.pinHash !== null;
  }

  /**
   * Check if PIN is enabled (alias for isPINConfigured)
   */
  isPINEnabled(): boolean {
    return this.isPINConfigured();
  }

  /**
   * Check if app is currently locked
   */
  isAppLocked(): boolean {
    return this.isLocked;
  }

  /**
   * Get lock-on-start setting
   */
  getLockOnStart(): boolean {
    const config = StorageService.get<PINConfig>('pin_config');
    return config?.lockOnStart ?? false;
  }

  /**
   * Generate random salt for PBKDF2
   */
  private generateSalt(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  }

  /**
   * Convert Uint8Array to base64 string
   */
  private arrayToBase64(array: Uint8Array): string {
    return btoa(String.fromCharCode.apply(null, Array.from(array)));
  }

  /**
   * Convert base64 string to Uint8Array
   */
  private base64ToArray(base64: string): Uint8Array {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Hash PIN using PBKDF2 with salt
   * @param pin - The PIN to hash
   * @param salt - The salt to use (if not provided, generates new salt)
   * @returns Object containing hash and salt
   */
  private async hashPIN(
    pin: string,
    salt?: Uint8Array
  ): Promise<{ hash: string; salt: string; iterations: number }> {
    const encoder = new TextEncoder();
    const pinData = encoder.encode(pin);

    // Generate salt if not provided
    const saltBytes = salt || this.generateSalt();

    // Import PIN as key material
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      pinData,
      'PBKDF2',
      false,
      ['deriveBits']
    );

    // Derive hash using PBKDF2
    const hashBuffer = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: saltBytes,
        iterations: PBKDF2_ITERATIONS,
        hash: 'SHA-256',
      },
      keyMaterial,
      256 // 256 bits output
    );

    // Convert hash to hex
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return {
      hash: hashHex,
      salt: this.arrayToBase64(saltBytes),
      iterations: PBKDF2_ITERATIONS,
    };
  }

  /**
   * Validate PIN format (4-6 digits)
   */
  private validatePINFormat(pin: string): boolean {
    const pinRegex = /^\d{4,6}$/;
    return pinRegex.test(pin);
  }

  /**
   * Create/Update PIN
   */
  async createPIN(pin: string, lockOnStart: boolean = true): Promise<boolean> {
    console.log('🔢 Creating/updating PIN...');

    // Validate PIN format
    if (!this.validatePINFormat(pin)) {
      console.error('❌ Invalid PIN format (must be 4-6 digits)');
      throw new Error('Le PIN doit contenir 4 à 6 chiffres');
    }

    try {
      // Hash the PIN with PBKDF2 + random salt
      const { hash, salt, iterations } = await this.hashPIN(pin);
      console.log('✅ PIN hashed successfully with PBKDF2');

      // Save to storage
      const config: PINConfig = {
        pinHash: hash,
        salt,
        iterations,
        createdAt: new Date().toISOString(),
        lockOnStart,
      };

      StorageService.set('pin_config', config);
      this.pinHash = hash;

      console.log(`✅ PIN saved to storage (${iterations} iterations)`);

      return true;
    } catch (error) {
      console.error('❌ Error creating PIN:', error);
      throw error;
    }
  }

  /**
   * Verify PIN
   */
  async verifyPIN(pin: string): Promise<boolean> {
    console.log('🔢 Verifying PIN...');

    // 🔒 SECURITY: Check rate limiting BEFORE processing
    const rateLimitCheck = this.isRateLimited();
    if (rateLimitCheck.limited) {
      console.warn('⚠️ Rate limited:', rateLimitCheck.reason);
      throw new Error(rateLimitCheck.reason);
    }

    if (!this.pinHash) {
      console.error('❌ No PIN configured');
      return false;
    }

    try {
      // Update last attempt time
      this.rateLimitState.lastAttemptTime = Date.now();
      this.saveRateLimitState();

      // Get stored PIN config
      const config = StorageService.get<PINConfig>('pin_config');
      if (!config || !config.salt) {
        console.error('❌ PIN config not found or invalid');
        return false;
      }

      // Hash the entered PIN with stored salt
      const saltBytes = this.base64ToArray(config.salt);
      const { hash: enteredHash } = await this.hashPIN(pin, saltBytes);

      // Compare hashes (constant-time comparison to prevent timing attacks)
      const isValid = enteredHash === config.pinHash;

      if (isValid) {
        console.log('✅ PIN verified successfully');
        this.isLocked = false;
        // Mark session as unlocked so we don't re-lock on navigation/refresh
        sessionStorage.setItem('unlocked_session', 'true');
        // Reset rate limit on success
        this.resetRateLimit();
        return true;
      } else {
        console.log('❌ Invalid PIN');
        // 🔒 SECURITY: Record failed attempt
        this.recordFailedAttempt();
        return false;
      }
    } catch (error) {
      console.error('❌ Error verifying PIN:', error);
      // Don't record as failed attempt if it's a system error
      if (error instanceof Error && !error.message.includes('tentatives')) {
        throw error;
      }
      return false;
    }
  }

  /**
   * Change lock-on-start setting
   */
  setLockOnStart(enabled: boolean): void {
    const config = StorageService.get<PINConfig>('pin_config');
    if (!config) {
      console.error('❌ No PIN configured');
      return;
    }

    config.lockOnStart = enabled;
    StorageService.set('pin_config', config);
    console.log('✅ Lock-on-start:', enabled);
  }

  /**
   * Delete PIN (disable PIN security)
   */
  deletePIN(): void {
    console.log('🔢 Deleting PIN...');
    StorageService.remove('pin_config');
    this.pinHash = null;
    this.isLocked = false;
    console.log('✅ PIN deleted');
  }

  /**
   * Unlock app (after successful PIN/biometric auth)
   */
  unlock(): void {
    console.log('🔓 Unlocking app...');
    this.isLocked = false;
    // Mark session as unlocked
    sessionStorage.setItem('unlocked_session', 'true');
    // Reset rate limit on successful unlock
    this.resetRateLimit();
  }

  /**
   * Lock app manually
   */
  lock(): void {
    console.log('🔒 Locking app...');
    if (this.isPINConfigured()) {
      this.isLocked = true;
      // Clear session unlock flag when manually locking
      sessionStorage.removeItem('unlocked_session');
      // Reset rate limit when manually locking
      this.resetRateLimit();
    }
  }

  /**
   * 🔒 SECURITY: Get remaining lockout time (for UI display)
   */
  getRemainingLockoutTime(): number {
    if (this.rateLimitState.lockedUntil) {
      const remaining = this.rateLimitState.lockedUntil - Date.now();
      return remaining > 0 ? remaining : 0;
    }
    return 0;
  }

  /**
   * 🔒 SECURITY: Get failed attempts count (for UI display)
   */
  getFailedAttemptsCount(): number {
    return this.rateLimitState.failedAttempts;
  }
}

// Export singleton instance
export const pinService = new PINService();
